import React, { useCallback, useEffect, useMemo, useState } from "react";
import moment from "moment";
import { useLazyQuery } from "@apollo/client/react";
import { useLocation, useNavigate } from "react-router";
import Alert from "@mui/material/Alert";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import Divider from "@mui/material/Divider";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Typography from "@mui/material/Typography";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Header from "../../../components/wizard/Header";
import Footer from "../../../components/wizard/Footer/Footer";
import useAuth from "../../../hooks/useAuth";
import EditableDateRangeFilter from "../../../views/favoriteReport/EditableDateRangeFilter";
import { PROJECT_BILLING_TOTALS_SUMMARY_QUERY } from "../../../constants/graphql/queries";

const UI_DATE_FORMAT = "MM/DD/YYYY";
const MODAL_TITLE = "Billing Totals";
const BASE_SUMMARY_CATEGORIES = ["inbound", "outbound", "ancillary"];

export const BILLING_TOTALS_PARAMS = {
  open: "billingTotals",
  step: "billingTotalsStep",
  mode: "billingTotalsMode",
  startDate: "billingTotalsStartDate",
  endDate: "billingTotalsEndDate",
};

const formatNumber = value =>
  Number(value || 0).toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });

const formatCurrency = value =>
  Number(value || 0).toLocaleString(undefined, {
    style: "currency",
    currency: "USD",
  });

const toIsoDate = value => {
  if (!value) return null;
  const parsed = moment(value, UI_DATE_FORMAT, true);
  return parsed.isValid() ? parsed.format("YYYY-MM-DD") : null;
};

const toUiDate = value => {
  if (!value) return null;
  const parsed = moment(value, "YYYY-MM-DD", true);
  return parsed.isValid() ? parsed.format(UI_DATE_FORMAT) : null;
};

const stepFromParams = params => {
  const step = parseInt(params.get(BILLING_TOTALS_PARAMS.step), 10);
  return [0, 1, 2].includes(step) ? step : 0;
};
const allTimeFromParams = params => params.get(BILLING_TOTALS_PARAMS.mode) === "all";
const settingEnabled = value => value === true || value === "true";

function SummaryRow({ label, value, secondary, emphasize = false }) {
  return (
    <Box display="flex" justifyContent="space-between" alignItems="baseline" gap={2}>
      <Box>
        <Typography
          component="span"
          color="text.secondary"
          fontWeight={emphasize ? 500 : 400}
        >
          {label}
        </Typography>
        {secondary && (
          <Typography component="span" color="text.disabled" fontSize={13} ml={1}>
            {secondary}
          </Typography>
        )}
      </Box>
      <Typography fontWeight={500} textAlign="right">
        {value}
      </Typography>
    </Box>
  );
}

function SectionCard({ section }) {
  const hasCharges = Number(section.totalCharges) > 0 || section.recordCount > 0;
  const totalLabel = section.title.endsWith("Charges")
    ? `Total ${section.title}`
    : `Total ${section.title} Charges`;

  return (
    <Box border={1} borderColor="divider" borderRadius={1} p={2.5} mb={2}>
      <Box display="flex" justifyContent="space-between" alignItems="baseline" mb={1.5}>
        <Typography fontWeight={500}>{section.title}</Typography>
        <Typography color="text.secondary" fontSize={14}>
          {formatNumber(section.recordCount)} {section.recordLabel}
        </Typography>
      </Box>

      {!hasCharges ? (
        <Typography color="text.disabled" fontSize={14}>
          No billing activity in this period.
        </Typography>
      ) : (
        <>
          {section.billingType && (
            <SummaryRow label="Billing Type" value={section.billingType} />
          )}
          {section.rows.map(row => (
            <SummaryRow
              key={row.label}
              label={row.label}
              secondary={
                [
                  row.quantity != null
                    ? `${formatNumber(row.quantity)}${
                        row.quantityUnit ? ` ${row.quantityUnit}` : ""
                      }`
                    : null,
                  row.rate != null ? `@ ${formatCurrency(row.rate)}` : null,
                  row.note,
                ]
                  .filter(Boolean)
                  .join(" · ") || null
              }
              value={formatCurrency(row.total)}
            />
          ))}
          <Divider sx={{ my: 1.5, borderStyle: "dotted" }} />
          <SummaryRow
            label={totalLabel}
            value={formatCurrency(section.totalCharges)}
            emphasize
          />
        </>
      )}
    </Box>
  );
}

function DottedDivider() {
  return <Divider sx={{ my: 1.5, borderStyle: "dotted" }} />;
}

function DetailedInboundCard({ detail }) {
  if (!detail) return null;

  return (
    <Box border={1} borderColor="divider" borderRadius={1} p={2.5} mb={2}>
      <Typography fontWeight={500} mb={1.5}>
        Inbound
      </Typography>

      {detail.billingType && (
        <SummaryRow label="Billing Type" value={detail.billingType} />
      )}
      {detail.inboundHundredweightRate != null && (
        <SummaryRow
          label="Inbound Hundredweight Rate"
          value={formatCurrency(detail.inboundHundredweightRate)}
        />
      )}
      {detail.inboundMinimumChargeRate != null && (
        <SummaryRow
          label="Inbound Minimum Charge Rate"
          value={formatCurrency(detail.inboundMinimumChargeRate)}
        />
      )}
      <SummaryRow label="Shipment Count" value={formatNumber(detail.shipmentCount)} />
      <DottedDivider />
      <SummaryRow
        label="Total Inbound Weight"
        value={`${formatNumber(detail.totalWeight)} lbs`}
      />
      <SummaryRow
        label="Non-Minimum Charge Weight"
        value={`${formatNumber(detail.nonMinimumChargeWeight)} lbs`}
      />
      <SummaryRow
        label="Minimum Charge Shipment Count"
        value={formatNumber(detail.minimumChargeShipmentCount)}
      />
      <DottedDivider />
      <SummaryRow
        label="Non-Minimum Charges Total"
        value={formatCurrency(detail.nonMinimumChargeTotal)}
      />
      <SummaryRow
        label="Minimum Charge Total"
        value={formatCurrency(detail.minimumChargeTotal)}
      />
      <DottedDivider />
      <SummaryRow
        label="Total Inbound Charges"
        value={formatCurrency(detail.totalInboundCharges)}
        emphasize
      />
    </Box>
  );
}

function InboundSummaryCard({ detail, section }) {
  const billingType = detail?.billingType || section?.billingType;

  if (billingType === "Hundredweight") {
    return <DetailedInboundCard detail={detail} />;
  }

  return section ? <SectionCard section={section} /> : null;
}

export default function BillingTotalsModal({ projectId, onClose }) {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const initialParams = new URLSearchParams(location.search);
  const filterField2Label =
    user?.company?.settings?.filter_field2?.toLowerCase() || "project";
  const internalWorkOrdersEnabled = settingEnabled(user?.company?.settings?.internal_work_orders);
  const summaryCategories = useMemo(
    () =>
      internalWorkOrdersEnabled
        ? [...BASE_SUMMARY_CATEGORIES, "internal_work_orders"]
        : BASE_SUMMARY_CATEGORIES,
    [internalWorkOrdersEnabled]
  );

  const [step, setStep] = useState(() => stepFromParams(initialParams));
  const [allTime, setAllTime] = useState(() => allTimeFromParams(initialParams));
  const [startDate, setStartDate] = useState(() =>
    toUiDate(initialParams.get(BILLING_TOTALS_PARAMS.startDate))
  );
  const [endDate, setEndDate] = useState(() =>
    toUiDate(initialParams.get(BILLING_TOTALS_PARAMS.endDate))
  );
  const [validationError, setValidationError] = useState("");
  const [showAllBuckets, setShowAllBuckets] = useState(false);

  const [fetchSummary, { data, loading, error }] = useLazyQuery(
    PROJECT_BILLING_TOTALS_SUMMARY_QUERY,
    { fetchPolicy: "network-only" }
  );

  const summary = data?.projectBillingTotalsSummary;

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    setStep(stepFromParams(params));
    setAllTime(allTimeFromParams(params));
    setStartDate(toUiDate(params.get(BILLING_TOTALS_PARAMS.startDate)));
    setEndDate(toUiDate(params.get(BILLING_TOTALS_PARAMS.endDate)));
  }, [location.search]);

  const updateUrlParams = useCallback(
    (updates = {}, removals = []) => {
      const params = new URLSearchParams(location.search);
      params.set(BILLING_TOTALS_PARAMS.open, "true");

      Object.entries(updates).forEach(([key, value]) => {
        if (value === null || value === undefined || value === "") {
          params.delete(key);
        } else {
          params.set(key, value);
        }
      });

      removals.forEach(key => params.delete(key));
      navigate({ search: params.toString() }, { replace: true });
    },
    [location.search, navigate]
  );

  const setStepParam = useCallback(
    nextStep => {
      setStep(nextStep);
      updateUrlParams({ [BILLING_TOTALS_PARAMS.step]: String(nextStep) });
    },
    [updateUrlParams]
  );

  const setModeParam = useCallback(
    nextAllTime => {
      setAllTime(nextAllTime);
      setValidationError("");
      updateUrlParams(
        { [BILLING_TOTALS_PARAMS.mode]: nextAllTime ? "all" : "range" },
        nextAllTime
          ? [BILLING_TOTALS_PARAMS.startDate, BILLING_TOTALS_PARAMS.endDate]
          : []
      );
    },
    [updateUrlParams]
  );

  const setStartDateParam = useCallback(
    value => {
      setStartDate(value);
      setValidationError("");
      updateUrlParams({
        [BILLING_TOTALS_PARAMS.mode]: "range",
        [BILLING_TOTALS_PARAMS.startDate]: toIsoDate(value),
      });
    },
    [updateUrlParams]
  );

  const setEndDateParam = useCallback(
    value => {
      setEndDate(value);
      setValidationError("");
      updateUrlParams({
        [BILLING_TOTALS_PARAMS.mode]: "range",
        [BILLING_TOTALS_PARAMS.endDate]: toIsoDate(value),
      });
    },
    [updateUrlParams]
  );

  const handleFetch = useCallback(async () => {
    if (!allTime) {
      if (!startDate || !endDate) {
        setValidationError("Select a start and end date, or switch on All Time.");
        return;
      }

      const start = moment(startDate, UI_DATE_FORMAT, true);
      const end = moment(endDate, UI_DATE_FORMAT, true);
      if (start.isValid() && end.isValid() && start.isAfter(end)) {
        setValidationError("Start date must be on or before end date.");
        return;
      }
    }

    setValidationError("");
    const response = await fetchSummary({
      variables: {
        projectId,
        startDate: allTime ? null : toIsoDate(startDate),
        endDate: allTime ? null : toIsoDate(endDate),
        categories: summaryCategories,
      },
    });
    if (response?.data?.projectBillingTotalsSummary) {
      setStepParam(2);
    }
  }, [allTime, endDate, fetchSummary, projectId, setStepParam, startDate, summaryCategories]);

  useEffect(() => {
    if (step !== 2 || summary || loading || error) return;
    if (!allTime && (!startDate || !endDate)) {
      setStepParam(1);
      return;
    }

    fetchSummary({
      variables: {
        projectId,
        startDate: allTime ? null : toIsoDate(startDate),
        endDate: allTime ? null : toIsoDate(endDate),
        categories: summaryCategories,
      },
    });
  }, [
    allTime,
    endDate,
    error,
    fetchSummary,
    loading,
    projectId,
    setStepParam,
    startDate,
    step,
    summary,
    summaryCategories,
  ]);

  const renderIntro = () => (
    <Box textAlign="center">
      <Box mb={3} display="flex" justifyContent="center">
        <img
          src="/inbound-billing-totals.png"
          alt={MODAL_TITLE}
          style={{ maxWidth: "100%", maxHeight: 220, borderRadius: 8 }}
        />
      </Box>
      <Typography variant="body1" align="center" fontSize={17} sx={{ maxWidth: 520, mx: "auto" }}>
        Review this {filterField2Label}&apos;s billing totals across inbound and outbound
        activity{internalWorkOrdersEnabled ? ", plus internal work orders" : ""}. Choose a date
        range or view totals across all time.
      </Typography>
    </Box>
  );

  const renderCalculator = () => (
    <Box pt={2}>
      <Typography variant="body2" color="text.secondary" align="center" mb={4}>
        Choose the time period for the billing totals you want to view.
      </Typography>

      <ToggleButtonGroup
        exclusive
        size="small"
        value={allTime ? "all" : "range"}
        onChange={(event, value) => {
          if (!value) return;
          setModeParam(value === "all");
        }}
        sx={{ mb: 4, width: "82%", mx: "auto", display: "flex" }}
      >
        <ToggleButton value="range" sx={{ flex: 1 }}>
          Date Range
        </ToggleButton>
        <ToggleButton value="all" sx={{ flex: 1 }}>
          All Time
        </ToggleButton>
      </ToggleButtonGroup>

      {!allTime && (
        <EditableDateRangeFilter
          startDate={startDate}
          endDate={endDate}
          onStartDateChange={setStartDateParam}
          onEndDateChange={setEndDateParam}
        />
      )}

      {validationError && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {validationError}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Unable to fetch billing totals.
        </Alert>
      )}
    </Box>
  );

  const renderResults = () => {
    if (!summary && loading) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={280}>
          <Box display="flex" alignItems="center" gap={1}>
            <CircularProgress size={20} />
            <Typography color="text.secondary">Fetching billing totals...</Typography>
          </Box>
        </Box>
      );
    }

    const rangeLabel = summary?.allTime
      ? "All Time"
      : `${moment(summary?.startDate).format(UI_DATE_FORMAT)} – ${moment(
          summary?.endDate
        ).format(UI_DATE_FORMAT)}`;
    const inboundSection = (summary?.sections || []).find(section => section.key === "inbound");
    const allBucketSections = (summary?.sections || []).filter(
      section => section.key !== "inbound" && section.key !== "storage"
    );

    return (
      <Box>
        <Typography fontWeight={500} align="center" mb={2}>
          {rangeLabel}
        </Typography>
        <InboundSummaryCard detail={summary?.inboundDetail} section={inboundSection} />
        <Accordion
          disableGutters
          expanded={showAllBuckets}
          onChange={(event, expanded) => setShowAllBuckets(expanded)}
          sx={{
            mb: 2,
            boxShadow: "none",
            border: 1,
            borderColor: "divider",
            borderRadius: 1,
            "&:before": { display: "none" },
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{ px: 2.5, py: 0.5, "& .MuiAccordionSummary-content": { my: 1 } }}
          >
            <Box>
              <Typography fontWeight={500}>All Other Billing Buckets</Typography>
              <Typography color="text.secondary" fontSize={14}>
                Expand to review outbound
                {internalWorkOrdersEnabled ? ", internal work order," : ""} and ancillary totals.
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ px: 2.5, pb: 2 }}>
            {allBucketSections.map(section => (
              <SectionCard key={section.key} section={section} />
            ))}
          </AccordionDetails>
        </Accordion>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="baseline"
          px={2.5}
          py={1}
        >
          <Typography fontWeight={700}>Grand Total</Typography>
          <Typography fontWeight={700}>{formatCurrency(summary?.grandTotal)}</Typography>
        </Box>
      </Box>
    );
  };

  const renderActions = () => {
    if (step === 0) {
      return (
        <Button variant="contained" color="primary" onClick={() => setStepParam(1)}>
          Start
        </Button>
      );
    }

    if (step === 1) {
      return (
        <Box display="flex" gap={1}>
          <Button color="inherit" onClick={() => setStepParam(0)}>
            Back
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleFetch}
            disabled={loading}
            startIcon={loading ? <CircularProgress color="inherit" size={18} /> : null}
            sx={{ minWidth: 160 }}
          >
            View Totals
          </Button>
        </Box>
      );
    }

    return (
      <Button variant="outlined" color="primary" onClick={() => setStepParam(1)}>
        Change Time Period
      </Button>
    );
  };

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth={false}
      sx={{ "& .MuiDialog-paper": { maxWidth: 560, width: "100%" } }}
    >
      <Box display="flex" flexDirection="column" minHeight={460}>
        <Header title={MODAL_TITLE} onClose={onClose} />
        <DialogContent sx={{ px: 3, py: 2, flexGrow: 1 }}>
          {step === 0 && renderIntro()}
          {step === 1 && renderCalculator()}
          {step === 2 && renderResults()}
        </DialogContent>
        <Footer step={step} steps={3} showRaceMode={false}>
          {renderActions()}
        </Footer>
      </Box>
    </Dialog>
  );
}
